import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import PaymentModal from '@/components/PaymentModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { BarChart, Upload, Loader2, ArrowLeft, CheckCircle, Users } from 'lucide-react';

const LiveStreamingServicePage = () => {
  const { user, isAdminAdil } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    video_title: '',
    youtube_link: '',
    video_file: null,
    notes: ''
  });
  const [videoFile, setVideoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [uploadMethod, setUploadMethod] = useState('youtube'); // 'youtube' ou 'upload'

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (uploadMethod === 'youtube' && !formData.youtube_link) {
      toast.error('Veuillez entrer le lien YouTube de votre clip');
      return;
    }
    
    if (uploadMethod === 'upload' && !videoFile) {
      toast.error('Veuillez uploader votre vidéo');
      return;
    }
    
    // Si Admin Adil, skip le paiement et soumettre directement
    if (isAdminAdil) {
      await handleDirectSubmit();
    } else {
      setShowPaymentModal(true);
    }
  };

  const handleDirectSubmit = async () => {
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('video_title', formData.video_title);
      formDataToSend.append('youtube_link', formData.youtube_link);
      formDataToSend.append('notes', formData.notes);
      formDataToSend.append('upload_method', uploadMethod);
      formDataToSend.append('is_admin_free', 'true');
      
      if (videoFile) {
        formDataToSend.append('video', videoFile);
      }
      
      await api.post('/service/payment/live-streaming', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSubmitted(true);
      toast.success('✅ Commande GRATUITE enregistrée ! Votre clip sera diffusé en LIVE sous 24h.');
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentInitiate = async (paymentData) => {
    const formDataToSend = new FormData();
    formDataToSend.append('video_title', formData.video_title);
    formDataToSend.append('youtube_link', formData.youtube_link);
    formDataToSend.append('notes', formData.notes);
    formDataToSend.append('upload_method', uploadMethod);
    formDataToSend.append('operator', paymentData.operator);
    formDataToSend.append('phone_number', paymentData.phone_number);
    
    if (videoFile) {
      formDataToSend.append('video', videoFile);
    }
    
    const response = await api.post('/service/payment/live-streaming', formDataToSend, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    return response.data;
  };

  const handlePaymentSuccess = () => {
    setSubmitted(true);
    toast.success('✅ Paiement réussi ! Votre clip sera diffusé en LIVE sous 24h.');
  };

  if (submitted) {
    return (
      <div className="min-h-screen pb-12">
        <Header />
        
        <main className="pt-24 max-w-3xl mx-auto px-4">
          <Card className="glass border-zinc-800 text-center">
            <CardContent className="py-12">
              <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
              <h2 className="text-2xl font-bold text-white mb-3">
                Paiement Réussi ! 🎉
              </h2>
              <p className="text-zinc-400 mb-6">
                Votre clip sera diffusé en LIVE avec plus de 3000 spectateurs garantis !
              </p>
              <div className="bg-zinc-900 rounded-lg p-4 mb-6">
                <p className="text-sm text-zinc-400">
                  <strong className="text-white">Prochaines étapes :</strong>
                </p>
                <ul className="text-left text-sm text-zinc-400 mt-2 space-y-1">
                  <li>✓ Paiement confirmé (50$)</li>
                  <li>✓ Votre clip est en file d'attente</li>
                  <li>⏳ Diffusion LIVE sous 24-48h</li>
                  <li>👥 +3000 spectateurs garantis</li>
                  <li>📧 Vous recevrez un email avec l'heure exacte du LIVE</li>
                </ul>
              </div>
              <Button onClick={() => navigate('/dashboard')} className="bg-[#E50914]">
                Retour au Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
        
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          amount={50}
          serviceName="Diffusion LIVE"
          onPaymentInitiate={handlePaymentInitiate}
          onPaymentSuccess={handlePaymentSuccess}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <Header />
      
      <main className="pt-24 max-w-3xl mx-auto px-4">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="mr-2" size={18} />
          Retour
        </Button>

        <Card className="glass border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-2xl">
              <BarChart className="text-cyan-500" size={32} />
              Passer Mon Clip en LIVE - {isAdminAdil ? 'GRATUIT (Admin Adil)' : '50$'}
            </CardTitle>
            <p className="text-zinc-400 text-sm mt-2">
              Diffusez votre clip en direct avec AGRP BOX et touchez +3000 spectateurs garantis !
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label>Titre de votre Clip *</Label>
                <Input
                  value={formData.video_title}
                  onChange={(e) => setFormData({...formData, video_title: e.target.value})}
                  required
                  placeholder="Ex: Mon dernier clip"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div>
                <Label className="mb-3 block">Comment voulez-vous partager votre clip ?</Label>
                <div className="flex gap-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setUploadMethod('youtube')}
                    className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                      uploadMethod === 'youtube'
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <span className="font-semibold">📺 Lien YouTube</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setUploadMethod('upload')}
                    className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                      uploadMethod === 'upload'
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <span className="font-semibold">📤 Upload Vidéo</span>
                  </button>
                </div>
              </div>

              {uploadMethod === 'youtube' ? (
                <div>
                  <Label>Lien YouTube de votre Clip *</Label>
                  <Input
                    type="url"
                    value={formData.youtube_link}
                    onChange={(e) => setFormData({...formData, youtube_link: e.target.value})}
                    required={uploadMethod === 'youtube'}
                    placeholder="https://youtube.com/watch?v=..."
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
              ) : (
                <div>
                  <Label>Upload Vidéo (MP4, MOV) *</Label>
                  <div className="mt-2">
                    <label className="flex items-center justify-center w-full p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-cyan-500 transition-colors bg-zinc-900">
                      <div className="text-center">
                        <Upload className="mx-auto mb-2 text-zinc-500" size={32} />
                        <p className="text-sm text-zinc-400">
                          {videoFile ? videoFile.name : 'Cliquez pour uploader votre clip'}
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => setVideoFile(e.target.files[0])}
                        className="hidden"
                        required={uploadMethod === 'upload'}
                      />
                    </label>
                  </div>
                </div>
              )}

              <div>
                <Label>Notes / Instructions (optionnel)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Heure souhaitée, message spécial, etc."
                  className="bg-zinc-900 border-zinc-800 text-white"
                  rows={3}
                />
              </div>

              <div className="bg-cyan-900/20 border border-cyan-800 rounded-lg p-4 text-sm text-cyan-200">
                <strong className="flex items-center gap-2">
                  <Users size={20} />
                  Ce que vous obtenez :
                </strong>
                <ul className="mt-2 space-y-1 ml-4 list-disc">
                  <li>✅ Diffusion en LIVE de votre clip</li>
                  <li>👥 +3000 spectateurs garantis en direct</li>
                  <li>⏰ Diffusion sous 24-48h</li>
                  <li>📊 Rapport de performance après le LIVE</li>
                  <li>🎯 Exposition maximale pour votre musique</li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className={`w-full ${isAdminAdil ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-cyan-600 to-cyan-800'} hover:opacity-90 py-6 text-lg font-semibold`}
              >
                {submitting ? <Loader2 className="animate-spin" /> : (isAdminAdil ? 'GRATUIT (Admin Adil) - Réserver' : 'RÉSERVER MON LIVE (50$)')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
      
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        amount={50}
        serviceName="Diffusion LIVE"
        onPaymentInitiate={handlePaymentInitiate}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default LiveStreamingServicePage;
